import { useEffect, useState, useRef, useCallback } from "react";
import useAuthentication from "../../hooks/useAuthentication.hook";
import NeoGridContainer from "../../design/design_components/neo/layout/NeoGridContainer.base";
import NeoCard from "../../design/design_components/neo/panel/NeoCard.base";
import NeoColumn from "../../design/design_components/neo/layout/NeoColumn.base";
import ContainedImages from "../../components/ContainedImages.component";
import { DateTime } from "luxon";
import Icono from "../../design/assets/img/bcn/live.png";
import MapReset from "../../design/assets/img/bcn/map_reset.png";
import Circle from "../../components/Circle.component";
import Avatar from "../../components/Avatar.component";
import useUpdateEffect from "../../hooks/useUpdateEffect.hook.js";
import AbsoluteText from "../../components/AbsoluteText.component";
import {
  TransformWrapper,
  TransformComponent,
  useControls,
} from "react-zoom-pan-pinch";
import NeoInputText from "design/design_components/neo/form/NeoInputText.base";
import NeoButtonMain from "design/design_components/neo/button/NeoButtonMain.base";
import avatar from "design/assets/img/wfi/avatar.svg"
import PreviewOverlay from "./components/PreviewOverlay";
import Player from "./components/Player";
import NeoMessages from "design/design_components/neo/messages/NeoMessages.base";
import NeoCalendar from "design/design_components/neo/form/NeoCalendar.base";
import NeoTable from "design/design_components/neo/table/NeoTable.base";
import { useQueries } from '@tanstack/react-query';
import * as BeaconService from "../../services/beacon.service";
import * as TrajectoryService from "../../services/trajectory.service";

const COLOR = {
  ACTIVE: '143, 190, 154',
  INACTIVE: '17, 156, 212',
  DISCONNECTED: '195, 195, 195'
}

const Controls = () => {
  const { zoomIn, zoomOut, resetTransform } = useControls();

  return (
    <div className="tools-zoom p-col-1 p-col-align-center">
      <div className="p-grid p-dir-col p-mx-auto">
        <button onClick={() => zoomIn()}>+</button>
        <hr/>
        <button onClick={() => zoomOut()}>-</button>
        <hr/>
        <button onClick={() => resetTransform()}><img src={MapReset}/></button>
      </div>
    </div>
  );
};

function calculateCurrPercentageOfPath(uniquePaths, slider) {
  return uniquePaths.map((curr, i, allPaths) => {
    const sliderPct = (slider.current / slider.length) * 100;
    const nextPlaceInTimeline = (100 / (allPaths.length - 1)) * (i + 1);
    let fillPct = 0;
   
    if (sliderPct > nextPlaceInTimeline) {
      fillPct = 100;
    } else {
      const currPlaceInTimeline = (100 / (allPaths.length - 1)) * i;
      const currLengthHere = nextPlaceInTimeline - currPlaceInTimeline;
      const currPctHere = sliderPct - currPlaceInTimeline;

      fillPct = (currPctHere / currLengthHere) * 100;
      if (sliderPct < currPlaceInTimeline) {
        fillPct = 0;
      }
    }

    curr.fillPct = fillPct;
    return curr;
  })
}

function calculateComputedValues(uniquePaths, currentPath, showPreview) {
  let biggestRangeOfPreviewPaths = [0, 0];
  
  let res =  {
    firstApInCurrFloorIndex: null,
    lastApInCurrFloorIndex: null,
    biggestRangeOfPreviewPath: biggestRangeOfPreviewPaths,
    apsVisibleInCurrentFloor: null,
    previewFloor: null
  };

  if (uniquePaths.length === 0) return res;

  
  function getActiveFloorApRangeIndices() {
    const currentPathIndex = uniquePaths.findIndex(
      (el) => el.path.id === currentPath.id
    );
    const range = [currentPathIndex,currentPathIndex];

    for (let i = currentPathIndex; i <= uniquePaths.length; i++) {
      const next = uniquePaths[i + 1];
      if (!next || next.path.store_floor_id !== currentPath.store_floor_id) {
        range[1] = i;
        break;
      }
    }
    
    for (let i = currentPathIndex; i >= 0; i--) {
      const prev = uniquePaths[i - 1];
      if (!prev || prev.path.store_floor_id !== currentPath.store_floor_id) {
        range[0] = i;
        break;
      }
    }

    return range; 
  }

  const [firstBeaconInCurrFloorIndex, lastBeaconInCurrFloorIndex] = getActiveFloorApRangeIndices();

  function getBiggestRangeOfPreviewPaths() {
    let biggestRange = [0, 0];
    let currentRange = [0, 0];
    let currentFloor = uniquePaths[0].path.store_floor_id;

    uniquePaths.forEach((path, index) => {
      if (path.path.store_floor_id === currentFloor) {
        currentRange[1] = index;
      } else {
        const [currStart, currEnd] = currentRange;
        const [biggestStart, biggestEnd] = biggestRange;
        const thereIsANextPath = index + 1 <= uniquePaths.length;

        if (thereIsANextPath) {
          if (currEnd + 1 - currStart > biggestEnd - biggestStart) {
            biggestRange = [currStart, currEnd + 1];
          }
        } else if (currEnd - currStart > biggestEnd - biggestStart) {
          biggestRange = currentRange;
        }

        currentRange = [index, index];
        currentFloor = path.path.store_floor_id;
      }
    });
    return biggestRange;
  }
  

  /** @type {string[]} */
  let allCurrFloorAPMinors = [];
  /** @type {Path|null} */
  let previewFloor = null;

  if (showPreview) {
    biggestRangeOfPreviewPaths = getBiggestRangeOfPreviewPaths();
    const [startI, endI] = biggestRangeOfPreviewPaths;
    previewFloor = uniquePaths[startI].path;

    allCurrFloorAPMinors = uniquePaths
      .slice(startI, endI)
      .map((el) => el.path.minor);
  } else {
    allCurrFloorAPMinors = uniquePaths
      .slice(firstBeaconInCurrFloorIndex, lastBeaconInCurrFloorIndex + 1)
      .map((el) => el.path.minor);
  }

  let beaconsVisibleInCurrentFloor = uniquePaths
    .map((el) => el.path.minor)
    .filter((mac) => allCurrFloorAPMinors.includes(mac));

  return {
    firstBeaconInCurrFloorIndex: firstBeaconInCurrFloorIndex,
    lastBeaconInCurrFloorIndex: lastBeaconInCurrFloorIndex,
    biggestRangeOfPreviewPaths: biggestRangeOfPreviewPaths,
    beaconsVisibleInCurrentFloor: beaconsVisibleInCurrentFloor,
    previewFloor: previewFloor
  };
}

function secondsToDuration(totalSegundos) {
  const horas = Math.floor(totalSegundos / 3600);
  const minutos = Math.floor((totalSegundos % 3600) / 60);
  const segundos = Math.floor(totalSegundos % 60); // Aplicamos Math.floor() aquí

  let resultado = "";

  if (horas > 0) {
    resultado += `${horas} hr`;
    if (minutos > 0) {
      resultado += ` ${minutos} min`;
      if (segundos > 0) {
        resultado += ` ${segundos} seg`;
      }
    } else if (segundos > 0) {
      resultado += ` ${segundos} seg`;
    }
  } else if (minutos > 0) {
    resultado += `${minutos} min`;
    if (segundos > 0) {
      resultado += ` ${segundos} seg`;
    }
  } else if (segundos > 0) {
    resultado += `${segundos} seg`;
  }

  return resultado;
}

export default function VisitDetails() {
  const authentication = useAuthentication();
  const [currentFloor, setCurrentFloor] = useState();
  const [slider, setSlider] = useState({ length: 0, current: 0 });
  const [paths, setPaths] = useState([]);
  const [img, setImg] = useState({
    clientWidth: 0,
    clientHeight: 0,
    naturalWidth: 0,
    naturalHeight: 0,
    spacingHorizontal: 0,
    spacingVertical: 0,
  });
  const [uniquePathSegments, setUniquePathSegments] = useState([]);
  const [currentPath, setCurrentPath] = useState({});
  const [showMap, setShowMap] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [isPlayerPlaying, setIsPlayerPlaying] = useState(false);
  const [allImagesLoaded, setAllImagesLoaded] = useState(false);

  const [emailToSearch, setEmailToSearch] = useState("");
  const [startDate, setStartDate] = useState(new Date(new Date().setHours(0, 0, 0, 0)));
  const [endDate, setEndDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const messagesRef = useRef();
  const [durations, setDurations] = useState([]);

  const results = useQueries({
    queries: [
      {
        queryKey: ['floors'],
        queryFn: async () => {
          return [
            {
              id: 1,
              name: "America Digital: Stand",
              url: "https://firebasestorage.googleapis.com/v0/b/neo-mnv-prod.appspot.com/o/neowifi%2Fplans%2FjbukGvOcs1RbBV9dbt2MANDrYAn2%2Fmapa-america-digital.jpg?alt=media&token=2d8908ad-f05a-4143-9575-b24b34198731"
            },
          ]
        },
        placeholderData: [],
      },
      {
        queryKey: ['beacons'],
        queryFn: BeaconService.get,
        select: (response) => {
          return response?.payload?.beacons ?? []
        },
        placeholderData: [],
      },
    ]
  });

  const isLoading = results.some(result => result.isLoading);
  const isError = results.some(result => result.isError);
  const isSuccess = results.every(result => result.isSuccess);

  const [floors, beacons] = results.map(result => result.data);

  useEffect(() => {
    if (isSuccess && floors.length > 0 && !currentFloor) {
      setCurrentFloor(floors[0]);
    }
  }, [isSuccess, floors, currentFloor]);

  const {
    firstBeaconInCurrFloorIndex,
    lastBeaconInCurrFloorIndex,
    biggestRangeOfPreviewPaths,
    apsVisibleInCurrentFloor,
    previewFloor, 
  } = calculateComputedValues(uniquePathSegments, currentPath, showPreview);

  const calculeCoords = useCallback((size, coord_x, coord_y) => {
    const scaleFactorY = (img.clientHeight - img.spacingVertical) / img.naturalHeight;
    const scaleFactorX = (img.clientWidth - img.spacingHorizontal) / img.naturalWidth;
    const mediaSize = Math.round(size / 2);
    return {
      coord_x: coord_x * scaleFactorX - mediaSize,
      coord_y: coord_y * scaleFactorY,
    }
  }, [img]);

  useEffect(() => {
    const sliderPct = (slider.current / slider.length) * 100;
    const step = 100 / (uniquePathSegments.length - 1);


    const currPath = uniquePathSegments.find((_, i) => {
      const nextPointInTimeline = step * (i + 1);
      const currPointInTimeline = step * i;

      return sliderPct >= currPointInTimeline && sliderPct < nextPointInTimeline;
    });

    setCurrentPath((prev) => currPath?.path ?? prev);

  }, [slider, uniquePathSegments]);

  useUpdateEffect(() => {
    if (showPreview) {
      setShowPreview(false);
    }
  },[slider.current]);

  useEffect(() => {
    // Change the fill percentage of the lines when the slider changes
    setUniquePathSegments((prev) => calculateCurrPercentageOfPath(prev, slider));
  }, [slider]);

  const fetchTrajectory = async () => {
    setLoading(true);
    const { success, payload, error } = await TrajectoryService.get({
      email: emailToSearch,
      startDate,
      endDate
    }).finally(() => {
      setLoading(false);
    });

    if (success) {
      const data = payload;
      const paths = data.events.map((event) => {
        const beacon = beacons.find((beacon) => beacon.minor === event.minor);
        if (beacon) {
          return {
            ...event,
            coord_x: beacon.coord_x,
            coord_y: beacon.coord_y,
            createdAt: new Date(event.createdAt),
            store_floor_id: 1,
            deparment_name: beacon.deparment_name
          }
        }
      }).filter((path) => path);
      setPaths(paths);
      if (paths.length) {
        const [firstPath] = paths;
        setCurrentPath(firstPath);
        const [lastPath] = paths.slice(-1);
        let secondsBeetwenStartAndEnd = 0;
        if (lastPath) {
          secondsBeetwenStartAndEnd = getTimeDifference(firstPath.createdAt, lastPath.createdAt);
          const min = 300;
          if (secondsBeetwenStartAndEnd < min) {
            secondsBeetwenStartAndEnd = min;
          }
        }
        setSlider((slider) => ({
          ...slider,
          length: secondsBeetwenStartAndEnd,
        }));
      } else {
        messagesRef.current.replace({
          severity: 'warn',
          detail: 'Este email no tiene visitas en el rango de fechas seleccionadas'
        });
      }
    } else {
      console.error("Error al buscar el email o ID", error);
    }
  }

  useEffect(() => {
    setShowMap(Boolean(paths.length));

    let filteredBeacons = paths.filter((currentPath, index, paths) => {
      if (index === 0) {
        return true;
      }
      const previousPath = paths[index - 1];
      return currentPath.minor !== previousPath.minor;
    });


    const scaleFactorY = (img.clientHeight - img.spacingVertical) / img.naturalHeight;
    const scaleFactorX = (img.clientWidth - img.spacingHorizontal) / img.naturalWidth;
    const unPaths = filteredBeacons.reduce((acc, path, index) => {
      if (img?.clientHeight !== 0 && img?.naturalHeight !== 0) {

        const coordY = path.coord_y * scaleFactorY;
        const coordX = path.coord_x * scaleFactorX;

        let dPath = `M ${coordX} ${coordY}`;
        let distance = 0;

        const next = filteredBeacons[index + 1];

        let nextCoordX = coordX;
        let nextCoordY = coordY;

        if (next) {

          nextCoordY =
          next?.coord_y * scaleFactorY;
          
          nextCoordX =
          next?.coord_x * scaleFactorX;
          
          distance = Math.sqrt(
            Math.pow(nextCoordX - coordX, 2) + Math.pow(nextCoordY - coordY, 2)
          );

          dPath += ` L ${nextCoordX} ${nextCoordY}`
        }

        const fillPct = 0;

        /** @type {UniquePathSegments} */
        const uniquePath = {
          dPath: dPath,
          length: distance,
          start: [coordX, coordY],
          end: [nextCoordX, nextCoordY],
          fillPct,
          path: path,
        }

        acc.push(uniquePath);
      }

      return acc;
    }, []);
    setUniquePathSegments(calculateCurrPercentageOfPath(unPaths, slider));
  }, [paths, img.clientHeight, img.clientWidth, img.naturalHeight, img.spacingHorizontal, img.naturalWidth])

  function calcAvatarPlacement() {
    if (uniquePathSegments.length === 0) return;
    
    if (uniquePathSegments.length === 1) {
      const { path } = uniquePathSegments[0];
      const { coord_x, coord_y } = calculeCoords(0, path.coord_x, path.coord_y);
      return [coord_x, coord_y];
    }

    const sliderPct = (slider.current / slider.length) * 100;
      const step = 100 / (uniquePathSegments.length - 1);
      let index = 0;

      const currPath = uniquePathSegments.find((_, i) => {
        const nextPointInTimeline = step * (i + 1);
        const currPointInTimeline = step * i;

        if (sliderPct >= currPointInTimeline && sliderPct < nextPointInTimeline) {
          index = i;
          return true;
        };
        return false;
      });

      if (!currPath) return [0,0];

      
      const currPlaceInTimeline = (100 / (uniquePathSegments.length - 1)) * index;
      const nextPlaceInTimeline = (100 / (uniquePathSegments.length - 1)) * (index + 1);
      const currLengthHere = nextPlaceInTimeline - currPlaceInTimeline;
      const currPctHere = sliderPct - currPlaceInTimeline;

      const fillPct = (currPctHere / currLengthHere) * 100;

      const [x1, y1] = currPath.start;
      const [x2, y2] = currPath.end;

      const x = x1 + ((x2 - x1) * (fillPct / 100));
      const y = y1 + ((y2 - y1) * (fillPct / 100));
      return [x,y]
  }

  const avatarCoords = calcAvatarPlacement();

  useEffect(() => {
    const durations = uniquePathSegments.map(({ path }, currentIndex, uniquePath) => {
      if (currentIndex !== (uniquePath.length - 1)) {
        const { path: nextPath } = uniquePath[currentIndex + 1];
        if (nextPath) {
          return {
            duration: getTimeDifference(nextPath.createdAt, path.createdAt),
            path,
          }
        }
      }
      return null;
    }).filter((duration) => duration);
    setDurations(durations);
  }, [uniquePathSegments])

  return (
    <>
      <div className="p-col-12">
        <div className="p-grid p-pl-4 p-pt-4 p-pr-4 p-align-center">
          <div className="p-col-12">
            <div className="p-d-flex">
              <img width="45" src={Icono} />
              <h1 className="titulo-pagina-principal p-pl-2">Detalle de la visita</h1>
            </div>
          </div>
          <div className="p-col-7">
            <div className="p-d-flex">
              <NeoInputText
                value={emailToSearch}
                label={"Email o ID de Negocio"}
                onChange={(event) => {
                  setEmailToSearch(event.target.value);
                }}
              />
              <NeoCalendar
                col={3}
                label="Fecha de inicio"
                value={startDate}
                showTime
                onChange={(event) => setStartDate(event.value)}
              />
              <NeoCalendar
                col={3}
                label="Frecha de fin"
                value={endDate}
                showTime
                onChange={(event) => setEndDate(event.value)}
              />
              <NeoButtonMain
                className="button-main"
                label='Buscar'
                onClick={fetchTrajectory}
                loading={loading}
                disabled={loading
                  || Boolean(emailToSearch) === false
                  || Boolean(startDate) === false
                  || Boolean(endDate) === false
                }
              />
            </div>
          </div>
        </div>
        <NeoMessages ref={messagesRef} />
      </div>
      
      {showMap &&  
        <NeoGridContainer>
          <NeoColumn extra="main-card" md={12}>
            <NeoCard extra="card-map" style={{ height: "100%" }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1fr",
                  width: "100%",
                  height: "100%",
                  padding: "0rem 1rem 0rem 1rem",
                }}
              >
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <div className="p-col-11">
                    <Player
                      slider={slider}
                      setSlider={setSlider}
                      uniquePaths={uniquePathSegments}
                      setModal={() => {}}
                      playing={isPlayerPlaying}
                      setPlaying={setIsPlayerPlaying}
                      style={{
                        marginBottom: `15px`,
                      }}
                    />
                  </div>
                  <TransformWrapper
                    initialScale={1}
                    initialPositionX={200}
                    initialPositionY={100}
                  >
                    {({ zoomIn, zoomOut, resetTransform, ...rest }) => (
                      <div className="p-grid">
                        <div className="p-col-11 p-py-5 p-col-align-center">
                          <TransformComponent>
                            <div className="preview-container" style={{ position: "relative", height: "600px" }}>

                              {/* Planos */}
                              <ContainedImages
                                allImagesLoaded={allImagesLoaded}
                                setAllImagesLoaded={setAllImagesLoaded}
                                floors={floors}
                                currentFloor={currentFloor}
                                setImg={setImg}
                              />

                              {/* Preview */}
                              {(showPreview && uniquePathSegments.length > 0) && (
                                <PreviewOverlay
                                  loading={!allImagesLoaded}
                                  onClickPlay={() => {
                                    setShowPreview(false);
                                    setIsPlayerPlaying(true);
                                  }}
                                />
                              )}

                              {/* Moving user in trajectory */}
                              {!showPreview && uniquePathSegments.length > 0 &&
                                <div style={{
                                  position: "absolute",
                                  zIndex: 3,
                                  top: avatarCoords[1] + img.spacingVertical / 2,
                                  left: avatarCoords[0] + img.spacingHorizontal / 2,
                                  transform: "translate(-50%, -70%)",
                                }}>
                                  <img alt="avatar" src={avatar}/>
                                </div>
                              }

                              {/* Trajectory */}
                              {allImagesLoaded && 
                                <svg
                                  width={img.clientWidth - (img.spacingHorizontal)}
                                  height={img.clientHeight - img.spacingVertical}
                                  style={{
                                    position: "absolute",
                                    top: "0",
                                    left: (img.spacingHorizontal / 2),
                                  }}
                                >
                                  {uniquePathSegments.map(({ dPath, fillPct, length }, i) => {
                                    const next = uniquePathSegments[i + 1];
                                    let isVisible = false;

                                    if (showPreview) {
                                      if (biggestRangeOfPreviewPaths) {
                                        const [startI, endI] = biggestRangeOfPreviewPaths;
                                        isVisible =
                                          showPreview && i >= startI && i < endI;
                                      }
                                    } else {
                                      if (i >= firstBeaconInCurrFloorIndex && i <= lastBeaconInCurrFloorIndex) {
                                        isVisible = true;
                                      }

                                      if (fillPct === 0) {
                                        isVisible = false;
                                      }

                                    }

                                    const fillAmount = {
                                      FULL: 0,
                                      NONE: length,
                                      PARTIAL: length - (fillPct / 100) * length
                                    }

                                    let currPathFillPct = fillAmount.NONE;
                                    

                                    if (showPreview) {
                                      if (biggestRangeOfPreviewPaths) {
                                        const [startI, endI] = biggestRangeOfPreviewPaths;
                                        const isPreviewPath =
                                          showPreview && i >= startI && i < endI;
                                        currPathFillPct = isPreviewPath
                                          ? fillAmount.FULL
                                          : fillAmount.NONE;
                                      }
                                    } else {
                                        const prev = uniquePathSegments[i - 1];
                                        if (prev && next) {
                                            if (fillPct === 0) {
                                              currPathFillPct = fillAmount.NONE;
                                            } else {
                                              currPathFillPct = fillAmount.PARTIAL;
                                            }
                                        } else {
                                          if (fillPct === 0) {
                                            currPathFillPct = fillAmount.NONE;
                                          } else {
                                            currPathFillPct = fillAmount.PARTIAL;
                                          }
                                        }
                                      
                                    }
                                  

                                    return (
                                      <path
                                        d={dPath}
                                        key={i}
                                        style={{
                                          fill: "transparent",
                                          stroke: "#79AFE3",
                                          strokeWidth: 5,
                                          strokeLinecap: "round",
                                          strokeLinejoin: "round",
                                          animationName: "draw",
                                          animationDuration: "1s",
                                          visibility: isVisible ? "visible" : "hidden",
                                          animationTimingFunction: "linear",
                                          animationFillMode: "forwards",
                                          animationPlayState: "paused",
                                          strokeDasharray: `${length}`,
                                          strokeDashoffset: `${currPathFillPct}`
                                        }}
                                      />
                                    );
                                  })
                                  }
                                </svg>
                              }
                              
                              {/* Lista de Beacons */}
                              <div
                                style={{
                                  width: `${img.clientWidth}px`,
                                  height: `${img.clientHeight - img.spacingVertical}px`,
                                  position: "absolute",
                                  top: 0,
                                  left: img.spacingHorizontal / 2,
                                }}
                              >
                                {allImagesLoaded &&
                                  beacons
                                    .filter(
                                      (beacon) =>
                                        beacon.store_floor_id === currentFloor.id
                                    )
                                    .filter(
                                      (beacon) =>
                                        Boolean(beacon.coord_x) &&
                                        beacon.coord_x > 0 &&
                                        Boolean(beacon.coord_y) &&
                                        beacon.coord_y > 0
                                    )
                                    .map((beacon, i) => {
                                      let color = COLOR.DISCONNECTED;
                                      if (uniquePathSegments && Array.isArray(uniquePathSegments)) {
                                        const usedOnPath = uniquePathSegments.find(o => o.path.minor === beacon.minor);
                                        if (usedOnPath) {
                                          color = COLOR.INACTIVE;
                                        }
                                      }
                                      
                                      return <>
                                        <AbsoluteText
                                          id={beacon.id}
                                          img={img}
                                          coord_x={beacon.coord_x}
                                          coord_y={beacon.coord_y-70}
                                          text={beacon.name}
                                        />
                                        <Circle
                                          id={beacon.id}
                                          tooltip={beacon.deparment_name}
                                          color={color}
                                          img={img}
                                          coord_x={beacon.coord_x}
                                          coord_y={beacon.coord_y}
                                        />
                                      </>
                                    })}
                              </div>
                            </div>
                          </TransformComponent>
                        </div>
                        <div className="p-col-1 p-col-align-center p-py-5">
                          <Controls />
                        </div>
                      </div>
                    )}
                  </TransformWrapper>
                </div>
                <div className="right-col">
                  <div className="table">
                    <NeoTable
                      value={durations}
                      emptyMessage=""
                      scrollable
                      scrollHeight="300px"
                      dataKey="beacon.minor"
                      header={(() => {
                        return <span>Ubicaciones principales</span>
                      })()}
                      footer={(() => {
                        const duration = durations.reduce((acc, {duration}) => {
                          acc = acc + duration;
                          return acc;
                        }, 0);
                        return <p>
                          Duración total estimada: <span className="bold">{secondsToDuration(duration)}</span>
                        </p>;
                      })()}
                      extra="table-durations"
                    >
                      <NeoColumn field="path.deparment_name" style={{width: '30%'}}/>
                      <NeoColumn style={{ width: '40%' }} body={(node, column) => {
                        if (node && node.path && node.path.createdAt) {
                          const createdAt = DateTime.fromJSDate(node.path.createdAt);
                          return <span>{createdAt.toLocaleString(DateTime.TIME_WITH_SECONDS)}</span>
                        }
                        return null;
                      }}/>
                      <NeoColumn style={{ width: '30%' }} body={(node, column) => {
                        if (node && node.duration) {
                          return secondsToDuration(node.duration);
                        }
                        return null;
                      }} />
                    </NeoTable>
                  </div>
                </div>
              </div>
            </NeoCard>
          </NeoColumn>
        </NeoGridContainer>
      }

    </>
  );
}

function getTimeDifference(date1, date2) {
  const diffInMilliseconds = Math.abs(date2 - date1);
  const diffInSeconds = diffInMilliseconds / 1000;
  return diffInSeconds;
}